import { router } from "expo-router";
import React, { useCallback, type ReactNode } from "react";
import { StyleSheet, Text, type StyleProp, type TextStyle } from "react-native";

type LegalRoute =
  | "/terms"
  | "/privacy"
  | "/refund-policy"
  | "/listing-guidelines";

type LegalLink = {
  label: string;
  pathname: LegalRoute;
};

type LegalPolicyLinkProps = {
  children: ReactNode;
  linkStyle?: StyleProp<TextStyle>;
  pathname: LegalRoute;
  returnTo: string;
};

type LegalPolicyFooterProps = {
  linkStyle?: StyleProp<TextStyle>;
  returnTo: string;
  style?: StyleProp<TextStyle>;
};

const LEGAL_LINKS: LegalLink[] = [
  { label: "Terms of Use", pathname: "/terms" },
  { label: "Privacy Policy", pathname: "/privacy" },
  { label: "Refund Policy", pathname: "/refund-policy" },
  { label: "Listing Guidelines", pathname: "/listing-guidelines" },
];

function LegalPolicyLink({
  children,
  linkStyle,
  pathname,
  returnTo,
}: LegalPolicyLinkProps) {
  const openPolicy = useCallback(() => {
    router.push({
      pathname,
      params: { returnTo },
    } as any);
  }, [pathname, returnTo]);

  return (
    <Text
      accessibilityRole="link"
      onPress={openPolicy}
      style={[styles.inlineLink, linkStyle]}
    >
      {children}
    </Text>
  );
}

export function ListingLegalAgreement({
  linkStyle,
  returnTo,
  style,
}: LegalPolicyFooterProps) {
  return (
    <Text style={[styles.text, style]}>
      {"By starting a listing, you agree to our "}
      <LegalPolicyLink
        linkStyle={linkStyle}
        pathname="/terms"
        returnTo={returnTo}
      >
        Terms of Use
      </LegalPolicyLink>
      {", "}
      <LegalPolicyLink
        linkStyle={linkStyle}
        pathname="/privacy"
        returnTo={returnTo}
      >
        Privacy Policy
      </LegalPolicyLink>
      {", "}
      <LegalPolicyLink
        linkStyle={linkStyle}
        pathname="/refund-policy"
        returnTo={returnTo}
      >
        Refund Policy
      </LegalPolicyLink>
      {", and "}
      <LegalPolicyLink
        linkStyle={linkStyle}
        pathname="/listing-guidelines"
        returnTo={returnTo}
      >
        Listing Guidelines
      </LegalPolicyLink>
      {"."}
    </Text>
  );
}

export function LegalPolicyFooter({
  linkStyle,
  returnTo,
  style,
}: LegalPolicyFooterProps) {
  return (
    <Text style={[styles.text, style]}>
      {"Legal: "}
      {LEGAL_LINKS.map((item, index) => (
        <React.Fragment key={item.pathname}>
          {index > 0
            ? index === LEGAL_LINKS.length - 1
              ? ", and "
              : ", "
            : ""}
          <LegalPolicyLink
            linkStyle={linkStyle}
            pathname={item.pathname}
            returnTo={returnTo}
          >
            {item.label}
          </LegalPolicyLink>
        </React.Fragment>
      ))}
      {"."}
    </Text>
  );
}

const styles = StyleSheet.create({
  text: {
    maxWidth: 720,
    fontSize: 12,
    lineHeight: 18,
    fontWeight: "700",
    color: "#64748b",
  },
  inlineLink: {
    color: "#0f766e",
    fontWeight: "900",
    textDecorationLine: "underline",
  },
});
